import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {AddressModel} from "../address/address.model";
import {ProjectModel, projectModelFactory, projectUpdateKeys} from "./project.model";
import {ProjectDetailListResponse, projectDetailListResponseFactory} from "./project-detail.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {pick} from "lodash";

const DEFAULT_PAGE = "1";
const DEFAULT_PAGE_LIMIT = "10";

@Injectable()
export class ProjectService {
  private url: string = "/api/projects";

  constructor(public http: HttpClient) {}

  getProjects(
    page: string = DEFAULT_PAGE,
    limit: string = DEFAULT_PAGE_LIMIT,
    search: string = "",
    limitByRegion: boolean = false,
    closed: boolean = false,
  ): Promise<ProjectDetailListResponse> {
    const params = {
      page,
      limit,
      search,
      limitByRegion: `${limitByRegion}`,
      closed: `${closed}`,
      sequence: "true",
    };

    return this.http.get<ProjectDetailListResponse>(`${this.url}`, {params})
      .pipe(map(projectDetailListResponseFactory))
      .toPromise();
  }

  getProjectById(id = ""): Observable<ProjectModel> {
    return this.http.get<ProjectModel>(`${this.url}/id?id=${id}`)
      .pipe(map(projectModelFactory));
  }

  saveProject(project: ProjectModel): Promise<ProjectModel> {
    const body = {...pick(project, projectUpdateKeys)};
    return this.http.post<ProjectModel>(`${this.url}/save`, body)
      .pipe(map(projectModelFactory))
      .toPromise();
  }

  saveShippingLabels(projectId, shippingLabel1, shippingLabel2): Promise<boolean> {
    return this.http.post(`${this.url}/save-shipping-label`, {projectId, shippingLabel1, shippingLabel2})
      .pipe(map(json => !!json["success"]))
      .toPromise();
  }

  updateGeoRegion(projectId, geoRegionId): Promise<boolean> {
    return this.http.post(`${this.url}/update-field`, {id: projectId, paramName: "geo_region_id", value: geoRegionId})
      .pipe(map(json => !!json["success"]))
      .toPromise();
  }

  saveDeliveryOpsInfo(
    project: any,
    billToAddress: AddressModel,
    billToContact,
  ): Observable<string> {
    const projectKeys = [
      "earlyOrderRec",
      "multiYear",
      "multiYearPoType",
      "multiYearTerm",
      "multiYearTermDuration",
      "plant",
      "prepaidFieldOnDorTab",
      "previousDocument",
      "renewal",
      "ruleDescription",
      "salesDocumentType",
      "legacyReportedDate",
      "multiYearEndDate",
      "multiYearStartDate",
      "dorRelavant",
      "endCustomerSplit",
      "payerSplit",
      "poNumberSplit",
      "shipToSplit",
      "id",
      "csa",
      "ampId",
      "msId",
      "orderAcceptance",
      "customerErpNumber",
      "handoverIdNumber",
      "opgNumber",
      "initialBillingDate",
      "billingStart",
      "legacyReportedDate",
      // "multiyearStartDate",
      // "multiyearEndDate",
      "enableCatalogPricing",
      "enableBulkOrdering",
      "orderAcceptanceDisableReason", // Only used on disabling orderAcceptance
      "orderAcceptanceCompleteDate",
    ];

    return this.http.post(`${this.url}/save-delivery-ops-info`, {
      ...pick(project, projectKeys),
      billToAddress,
      billToContact,
    })
      .pipe(map(() => project.id));
  }

  saveCustomer(id, customerId, customerName): Observable<string> {
    return this.http.post(`${this.url}/save-customer`, {id, customerId, customerName})
      .pipe(map(() => id));
  }

  unassignCustomer(id): Observable<any> {
    return this.http.post(`${this.url}/unassign-customer`, {id});
  }

  remove(id): Observable<any> {
    return this.http.post(`${this.url}/remove`, {id});
  }

  create(name: string, region?: string, targetSystem?: string): Observable<string> {
    return this.http.post(`${this.url}/create`, {name, region, targetSystem})
      .pipe(map(result => `${result}`));
  }

  assignUserToProject(projectId: string): Observable<any> {
    return this.http.post(`${this.url}/assign-user-to-project`, {projectId});
  }

  unAssignUserToProject(projectId: string): Observable<any> {
    return this.http.post(`${this.url}/unassign-user-to-project`, {projectId});
  }

  getAssignableSubstituteUsers(): Observable<any> {
    return this.http.post(`${this.url}/get-assignable-sub-users`, {});
  }

  crossProductUserProjectAssignment(projectIds, userIds): Observable<any> {
    return this.http.post(`${this.url}/assign-users-to-projects`, {projectIds, userIds});
  }

  getPartnerPurchaseOrders(projectId = ""): Promise<any> {
    return this.http.get(`${this.url}/getPartnerPurchaseOrders?id=${projectId}`)
      .toPromise();
  }

  purge(projectId: string = ""): Promise<boolean> {
    return this.http.post<{success: boolean}>(`${this.url}/purge`, {projectId})
      .pipe(map(result => result.success))
      .toPromise();
  }

  rejectProject(projectId: string, reason: string, workflowId: string): Promise<{success: boolean, message: string}> {
    const params = {projectId, reason, workflowId};

    return this.http.post<{success: boolean, message: string}>(`${this.url}/reject-project`, params)
      .pipe(map(({success, message}) => ({success, message})))
      .toPromise();
  }

  unRejectProject(projectId: string): Promise<{success: boolean, message: string}> {
    const params = {projectId};

    return this.http.post<{success: boolean, message: string}>(`${this.url}/un-reject-project`, params)
      .pipe(map(({success, message}) => ({success, message})))
      .toPromise();
  }

  saveSiteNameStructure(projectId: string, siteNameStructure: string): Promise<void> {
    const params = {projectId, siteNameStructure};
    return this.http.post<void>(`${this.url}/save-site-name-structure`, params).toPromise();
  }

  getProjectCountry(projectId: string): Promise<any> {
    return this.http.get(`${this.url}/project-country?id=${projectId}`)
      .toPromise();
  }

  getShippingLabels(projectId: string): Promise<any> {
    return this.http.get(`${this.url}/project-shipping-labels?id=${projectId}`)
      .toPromise();
  }
}
